import { Suspense, useCallback, useState, useRef } from "react";
import { useNodeId } from "@xyflow/react";
import { useAtomValue, useSetAtom, useAtom } from "jotai";
import { Button } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  updateNodeSetter,
  newAssetNameAtom,
  chatbotsAtom_SUSPENDS,
  chatbotUsageAtom_SUSPENDS,
  saveCanvasSetter,
  closeSecondaryToolbarMenuSetter,
} from "@zapier/canvas-state";
import { CREATE_NEW_CHATBOTS_BUTTON_TEXT } from "@zapier/canvas-constants";

import { useEmitResourceCreatedEvents } from "../../hooks/useEmitResourceCreatedEvents";
import { ErrorBoundary } from "../ErrorBoundary";
import { CreateAssetLoadingButton } from "../CreateAssetLoadingButton";
import { UpsellPopover } from "../UpsellPopover";

function SuspendedCreateNewChatbotButton() {
  const nodeId = useNodeId();
  const [createNewChatbotLoading, setCreateNewChatbotLoading] = useState(false);
  const name = useAtomValue(newAssetNameAtom);
  const refreshChatbots = useSetAtom(chatbotsAtom_SUSPENDS);
  const updateNode = useSetAtom(updateNodeSetter);
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);
  const { emitEventOnChatbotCreation } = useEmitResourceCreatedEvents();
  const [error, setError] = useState<Error | null>(null);
  // This is after creating a new chatbot fails
  const [needsToUpgrade, setNeedsToUpgrade] = useState(false);
  // This is data we fetch before trying to create new chatbot
  const [chatbotsUsage, refreshChatbotsUsage] = useAtom(
    chatbotUsageAtom_SUSPENDS,
  );
  const isUpgradeNeeded = needsToUpgrade || chatbotsUsage?.needsToUpgrade;

  // Popover state
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const createNewChatbot = useCallback(async () => {
    // If upgrade is needed, show popover instead of creating chatbot
    if (isUpgradeNeeded) {
      setIsPopoverOpen(true);
      return;
    }

    async function handleCreateNewChatbot() {
      setCreateNewChatbotLoading(true);
      try {
        setNeedsToUpgrade(false);
        const response = await createNewChatbotAsync(name);

        if ("errors" in response) {
          throw response;
        }

        const chatbotId = "data" in response ? response.data.id : null;

        if (chatbotId) {
          closeSecondaryMenu();

          const updatedNodes = updateNode({
            id: nodeId!,
            data: {
              chatbotId,
            },
          });

          saveToBackend({ nodes: updatedNodes });
          // Trigger chatbots to be re-fetched, so the new chatbot can be populated in the Chatbot Typeahead
          refreshChatbots();
          refreshChatbotsUsage(); // Because chatbot count would've changed

          emitEventOnChatbotCreation(chatbotId);
        }

        setError(null);
      } catch (e: unknown) {
        const error = e as ErrorObject;

        if (error && error.code === "chatbots:limits:chatbots") {
          setNeedsToUpgrade(true);
        } else {
          setError(
            new Error(
              "We ran into an unexpected error trying to create a new chatbot. Try again.",
            ),
          );
        }
      } finally {
        setCreateNewChatbotLoading(false);
      }
    }

    void handleCreateNewChatbot();
  }, [
    name,
    updateNode,
    nodeId,
    saveToBackend,
    refreshChatbots,
    emitEventOnChatbotCreation,
    refreshChatbotsUsage,
    closeSecondaryMenu,
    isUpgradeNeeded,
  ]);

  if (error) {
    throw error;
  }

  return (
    <>
      <Button
        // @ts-ignore
        ref={buttonRef}
        variant={isUpgradeNeeded ? "tertiary" : "primary"}
        size="compact"
        iconPosition="start"
        isPending={createNewChatbotLoading}
        onPress={createNewChatbot}
      >
        <Icon name="formAdd" />
        {CREATE_NEW_CHATBOTS_BUTTON_TEXT}
      </Button>
      {isUpgradeNeeded && (
        <UpsellPopover
          assetType="chatbot"
          currentPlanName={chatbotsUsage?.plan.name ?? "free"}
          triggerRef={buttonRef}
          isOpen={isPopoverOpen}
          onOpenChange={setIsPopoverOpen}
        />
      )}
    </>
  );
}

export function CreateNewChatbotButton() {
  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <CreateAssetLoadingButton>
            {CREATE_NEW_CHATBOTS_BUTTON_TEXT}
          </CreateAssetLoadingButton>
        }
      >
        <SuspendedCreateNewChatbotButton />
      </Suspense>
    </ErrorBoundary>
  );
}

type NewChatbotSuccessResponse = {
  data: {
    id: string;
    name: string;
  };
  code: string;
  detail: string;
};

type ErrorObject = {
  detail: string;
  code: string;
  errors: {
    status: number;
    code: string;
    detail: string;
  }[];
};

type NewChatbotErrorResponse = {
  error: ErrorObject;
};

type NewChatbotResponse = NewChatbotSuccessResponse | NewChatbotErrorResponse;

async function createNewChatbotAsync(name: string) {
  const response = await fetch(`/api/canvasui/chatbots`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
  return response.json() as Promise<NewChatbotResponse>;
}
